import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarClock, ChevronLeft, ChevronRight, FileSignature, Loader2, Plus } from "lucide-react";
import { useT } from "../../../i18n/I18nProvider";
import {
  deleteContract, getContracts, type Contract, type ContractStatus,
} from "../../../lib/projects";
import {
  Avatar, Btn, EmptyState, MenuField, SearchField, StatusPill, Toolbar, ToolbarGap, fmtDate, listCard,
} from "../../ui";
import { ConfirmModal } from "../_shared/ConfirmModal";
import { ContractRowMenu } from "./ContractRowMenu";
import { NewContractModal } from "./NewContractModal";
import { contractValue } from "./lib";
import { STATUSES, statusTone } from "./status";

/**
 * Number and title, client, status, term, value - five tracks plus the actions
 * column, used by both the head and the rows so a new column cannot shift one
 * without the other.
 */
const COLS = "minmax(0,2.3fr) 1.3fr .9fr 1.3fr .9fr 44px";

/** The design's page size, and the endpoint's ceiling is 100 (see `listQuery`
 *  in contracts.routes.ts), so this stays well inside it. */
const PAGE_SIZE = 25;

type StatusFilter = ContractStatus | "all";

/**
 * The contract register.
 *
 * Was a bordered table whose rows navigated from an `onClick` on the `<tr>`,
 * which meant no keyboard could reach a contract and a screen reader had nothing
 * to announce. Rows are pills with a real link now.
 */
export function ContractsList() {
  const t = useT();
  const [rows, setRows] = useState<Contract[]>([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [qInput, setQInput] = useState("");
  const [status, setStatus] = useState<StatusFilter>("all");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [showNew, setShowNew] = useState(false);
  const [confirmDel, setConfirmDel] = useState<Contract | null>(null);

  useEffect(() => {
    const id = setTimeout(() => setQ(qInput.trim()), 300);
    return () => clearTimeout(id);
  }, [qInput]);

  // Any change to what is being asked for starts at the first page. Staying on
  // page 4 of a result set that now has one page shows an empty card and looks
  // like the filter found nothing.
  useEffect(() => {
    setPage(1);
  }, [q, status]);

  // Both filters are answered by the endpoint. The list used to ask for a
  // hundred rows and never page, so an account past its hundredth contract
  // simply could not see the older ones.
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getContracts({
        page,
        pageSize: PAGE_SIZE,
        q: q || undefined,
        status: status === "all" ? undefined : status,
      });
      setRows(res.rows);
      setTotal(res.total);
    } finally {
      setLoading(false);
    }
  }, [q, status, page]);

  useEffect(() => {
    void load();
  }, [load]);

  async function remove(c: Contract) {
    await deleteContract(c.id);
    setRows((prev) => prev.filter((x) => x.id !== c.id));
    setTotal((n) => Math.max(0, n - 1));
    setConfirmDel(null);
  }

  return (
    <>
      <Toolbar>
        <SearchField value={qInput} onChange={setQInput} placeholder={t("contracts.search")} />
        <MenuField
          value={status}
          onChange={setStatus}
          label={t("contracts.statusCol")}
          options={[
            { value: "all" as const, label: t("contracts.allStatuses") },
            ...STATUSES.map((s) => ({ value: s, label: t(`contracts.status.${s}`) })),
          ]}
        />
        <ToolbarGap />
        <Btn tone="brand" onClick={() => setShowNew(true)}>
          <Plus className="size-4" />
          {t("contracts.add")}
        </Btn>
      </Toolbar>

      <div className="crm-card" style={listCard(COLS)}>
        {loading ? (
          <div className="flex justify-center py-16 text-muted-foreground">
            <Loader2 className="size-6 animate-spin" />
          </div>
        ) : rows.length === 0 ? (
          <EmptyState
            icon={FileSignature}
            title={t("contracts.empty")}
            body={t("contracts.emptyBody")}
            action={
              <Btn tone="brand" onClick={() => setShowNew(true)}>
                <Plus className="size-4" />
                {t("contracts.add")}
              </Btn>
            }
          />
        ) : (
          <>
            <div className="crm-list-head" style={{ gridTemplateColumns: COLS, paddingTop: 22 }}>
              <span>{t("contracts.number")}</span>
              <span>{t("contracts.client")}</span>
              <span>{t("contracts.statusCol")}</span>
              <span>{t("contracts.validity")}</span>
              <span>{t("contracts.value")}</span>
              <span />
            </div>

            <div className="crm-rows pt-0">
              {rows.map((c) => (
                <ContractRow key={c.id} contract={c} t={t} onDelete={() => setConfirmDel(c)} />
              ))}
            </div>

            <div className="crm-tfoot">
              {t("contracts.showing", {
                from: (page - 1) * PAGE_SIZE + 1,
                to: (page - 1) * PAGE_SIZE + rows.length,
                total,
              })}
              <span className="crm-push flex gap-2.5">
                <Btn small disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
                  <ChevronLeft className="size-4" />
                  {t("common.prev")}
                </Btn>
                <Btn small disabled={page * PAGE_SIZE >= total} onClick={() => setPage((p) => p + 1)}>
                  {t("common.next")}
                  <ChevronRight className="size-4" />
                </Btn>
              </span>
            </div>
          </>
        )}
      </div>

      {showNew ? <NewContractModal onClose={() => setShowNew(false)} onCreated={() => void load()} /> : null}

      {confirmDel ? (
        <ConfirmModal
          title={t("contracts.deleteTitle")}
          body={t("contracts.deleteConfirm")}
          confirmLabel={t("common.delete")}
          cancelLabel={t("common.cancel")}
          onConfirm={() => remove(confirmDel)}
          onClose={() => setConfirmDel(null)}
        />
      ) : null}
    </>
  );
}

/** The term as one cell. A contract with neither date shows a single dash
 *  rather than "- - -", which reads as a formatting fault. */
function term(c: Contract): string {
  if (!c.startDate && !c.endDate) return "-";
  return `${fmtDate(c.startDate)} - ${fmtDate(c.endDate)}`;
}

function ContractRow({
  contract,
  t,
  onDelete,
}: {
  contract: Contract;
  t: ReturnType<typeof useT>;
  onDelete: () => void;
}) {
  // Late only counts while the contract is still supposed to be running. One
  // that was terminated or has already been marked expired is not "overdue",
  // and painting it red forever turns the colour into wallpaper.
  const running = contract.status === "active" || contract.status === "sent";
  const late = running && !!contract.endDate && new Date(contract.endDate) < new Date();

  return (
    // A div, not a link: the row ends in a menu button, and interactive content
    // inside an <a> is invalid HTML and swallows the button's clicks. The number
    // link stretches an ::after over the whole row instead, so the row stays
    // clickable end to end and the button still sits on top of it.
    <div className="crm-list-row relative" style={{ gridTemplateColumns: COLS }}>
      <span className="flex min-w-0 items-center gap-3">
        {/* Initialled off the title rather than the number: a contract's title is
            the thing a person recognises, and initials made out of a serial
            number would be noise. */}
        <Avatar name={contract.title} />
        <span className="min-w-0">
          <Link
            to={`/contracts/${contract.id}`}
            className="crm-prow-n crm-tnum crm-trunc block after:absolute after:inset-0 after:rounded-full"
          >
            {contract.number ?? t("contracts.noNumber")}
          </Link>
          <span className="crm-prow-s crm-trunc block">{contract.title}</span>
        </span>
      </span>

      <span className="crm-mut crm-trunc text-[12.5px]">{contract.client?.name ?? "-"}</span>

      <span>
        <StatusPill tone={statusTone(contract.status)}>{t(`contracts.status.${contract.status}`)}</StatusPill>
      </span>

      <span
        className={`crm-tnum flex items-center gap-1.5 text-[12.5px] ${late ? "text-red-600" : "crm-mut"}`}
      >
        <CalendarClock className="size-3.5 shrink-0" />
        {term(contract)}
      </span>

      {/* The value agreed, not anything invoiced against it - the two are not
          linked in the schema, so a "billed" column here would be a guess. */}
      <span className="crm-tnum text-[13.25px] font-bold">{contractValue(contract.valueCents)}</span>

      <ContractRowMenu contract={contract} onDelete={onDelete} />
    </div>
  );
}
